<template>
  <div>
    <div class="flex items-center gap-2 min-w-0">
      <wui-input
        v-model="dateModel"
        type="date"
        :min="dateMin"
        prepend-icon="calendarM"
        rounded
        class="min-w-0 grow"
        @focus="isDateInputFocused = true"
        @blur="onDateBlur"
        @input="onDateInput"
      />
      <time-range-input
        v-model:time-from="timeFrom"
        v-model:time-to="timeTo"
        v-model:is-time-invalid="isTimeFormatInvalid"
        :is-today-selected="isTodaySelected"
        class="shrink-0"
      />
    </div>
    <p class="text-label-r-13-18 mb-1 text-fg-base-fg14 ml-1 mt-3">
      {{ t('common.repetition') }}
    </p>
    <wui-select
      v-model="periodMode"
      :items="periodModeItems"
      rounded
      primary
    />
    <div
      v-if="periodMode === PeriodMode.EVERY_SPECIAL_MONTH_DAY"
      class="mt-3"
    >
      <monthdays-select
        v-model="selectedDays"
        class="mt-2"
      />
      <p class="text-label-r-13-18 mb-1 text-fg-base-fg14 ml-1 mt-3">
        {{ t('chat.scheduleEnd') }}
      </p>
      <div class="flex items-center gap-2">
        <wui-select
          v-model="endDateMode"
          :items="endDateModeItems"
          rounded
          primary
        />
        <wui-input
          v-if="endDateMode === 'custom'"
          v-model="endDateModel"
          type="date"
          :min="endDateMin"
          prepend-icon="calendarM"
          rounded
          @focus="isEndDateInputFocused = true"
          @blur="onEndDateBlur"
          @input="onEndDateInput"
        />
      </div>
    </div>
    <weekdays-input
      v-else-if="periodMode === PeriodMode.EVERY_SPECIAL_WEEK_DAY"
      v-model="selectedWeekdays"
    />

    <template
      v-if="periodMode === PeriodMode.EVERY_DAY || periodMode === PeriodMode.EVERY_WORK_DAY || periodMode === PeriodMode.EVERY_SPECIAL_WEEK_DAY"
    >
      <repeat-input
        v-model="repeatOptions"
        :is-retry-days-visible="periodMode === PeriodMode.EVERY_DAY"
      />
    </template>
    <div
      v-if="summary"
      class="mt-3"
    >
      <p class="text-label-r-13-18 mb-1 text-fg-base-fg14 ml-1">
        {{ t('chat.total') }}
      </p>
      <p class="text-body-r-14-20 text-fg-base-fg3 ml-1">
        {{ summary }}
      </p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { WuiInput, WuiSelect, SelectItem } from '@wui/common-library'
import dayjs from 'dayjs'
import { ref, computed, onBeforeMount, onBeforeUnmount, onMounted, watch, nextTick } from 'vue'

import { PeriodMode } from '@/entities/schedule'

import { MonthdaysSelect } from '@/shared/components/monthdays-select'
import { useLocalization } from '@/shared/composables/localization'
import { getProfileTimezone } from '@/shared/composables/timezone'

import { _15_MINUTES_IN_MS } from '../consts'
import {
  canSyncDateInputFromModel,
  clampDateInputToMin,
  formatTimestampToDateInput,
  isCompleteDateInput,
  parseDateInputToStartOfDay,
  resolveStartDateMin,
  sanitizeDateInputYear,
} from '../lib/calendar-date'
import { generateSummary } from '../lib/generate-summary'
import {
  getDefaultScheduleTimeRange,
  isOnceScheduleStartInPast,
  isScheduleTimeUnset,
} from '../lib/schedule-time'
import { RepeatOptions, ScheduleDateTimeInputPayload } from '../types'

import RepeatInput from './repeat-input.vue'
import TimeRangeInput from './time-range-input.vue'
import WeekdaysInput from './weekdays-input.vue'

const props = withDefaults(defineProps<{
  modelValue: ScheduleDateTimeInputPayload
  isEdit?: boolean
  isTimeInvalid?: boolean
}>(), {
  isEdit: false,
  isTimeInvalid: false,
})

const periodMode = defineModel<PeriodMode>('periodMode', { required: true })

const emit = defineEmits<{
  (e: 'update:modelValue', payload: ScheduleDateTimeInputPayload): void
  (e: 'update:isTimeInvalid', value: boolean): void
}>()

const isTimeFormatInvalid = ref(false)

const { t } = useLocalization()

type EndDateMode = 'always' | 'custom'

const periodModeItems: SelectItem<PeriodMode>[] = [
  {
    label: t('calendar.notRepeat'),
    value: PeriodMode.ONCE,
  },
  {
    label: t('calendar.everyDay'),
    value: PeriodMode.EVERY_DAY,
  },
  {
    label: t('calendar.everyWorkDay'),
    value: PeriodMode.EVERY_WORK_DAY,
  },
  {
    label: t('calendar.everySpecialWeekDay'),
    value: PeriodMode.EVERY_SPECIAL_WEEK_DAY,
  },
  {
    label: t('calendar.everySpecialMonthDay'),
    value: PeriodMode.EVERY_SPECIAL_MONTH_DAY,
  },
]

const endDateModeItems: SelectItem<EndDateMode>[] = [
  {
    label: t('chat.scheduleRepeatAlways'),
    value: 'always',
  },
  {
    label: t('chat.scheduleSelectEndDate'),
    value: 'custom',
  },
]

const endDateMode = ref<EndDateMode>('always')
const repeatOptions = ref<RepeatOptions>({
  repeat: 'week',
  repeatDays: 1,
})
const selectedWeekdays = ref<number[]>([])
const selectedDays = ref<number[]>([])

const today = ref(dayjs().tz(getProfileTimezone()).format('YYYY-MM-DD'))
const date = ref<string>(dayjs().tz(getProfileTimezone()).format('YYYY-MM-DD'))
const endDateInput = ref<string>('')

const now = ref(dayjs().tz(getProfileTimezone()))
const defaultTimeRange = getDefaultScheduleTimeRange(now.value)
const timeFrom = ref<number>(
  isScheduleTimeUnset(props.modelValue.selectedTime.timeFrom)
    ? defaultTimeRange.timeFrom
    : props.modelValue.selectedTime.timeFrom,
)
const timeTo = ref<number>(
  isScheduleTimeUnset(props.modelValue.selectedTime.timeTo)
    ? defaultTimeRange.timeTo
    : props.modelValue.selectedTime.timeTo,
)

const dateMin = computed(() => resolveStartDateMin(
  today.value,
  props.modelValue.startDate,
  Boolean(props.isEdit),
))

const endDateMin = computed(() => {
  if (isCompleteDateInput(date.value)) {
    return date.value
  }

  if (props.modelValue.startDate) {
    return formatTimestampToDateInput(props.modelValue.startDate)
  }

  return today.value
})

const summary = computed(() => {
  return generateSummary(periodMode.value, {
    repeatOptions: {
      repeat: repeatOptions.value.repeat,
      repeatDays: repeatOptions.value.repeatDays,
    },
    startDate: startDate.value,
    endDate: endDate.value,
    selectedDays: selectedDays.value,
    selectedWeekdays: selectedWeekdays.value,
    selectedTime: {
      timeFrom: timeFrom.value,
      timeTo: timeTo.value,
    },
  })
})

const startDate = computed(() => (
  parseDateInputToStartOfDay(date.value) || props.modelValue.startDate || 0
))

const endDate = computed(() => {
  if (endDateMode.value !== 'custom' || !endDateInput.value) {
    return 0
  }

  return parseDateInputToStartOfDay(endDateInput.value) || props.modelValue.endDate || 0
})

const isTodaySelected = computed(() => (
  periodMode.value === PeriodMode.ONCE
  && isCompleteDateInput(date.value)
  && now.value.isSame(dayjs.tz(date.value, getProfileTimezone()), 'day')
))

const isPastOnceScheduleStart = computed(() => {
  if (periodMode.value !== PeriodMode.ONCE) {
    return false
  }

  if (!isCompleteDateInput(date.value) || !startDate.value) {
    return false
  }

  if (isScheduleTimeUnset(timeFrom.value)) {
    return false
  }

  return isOnceScheduleStartInPast(
    startDate.value,
    timeFrom.value,
    now.value,
    getProfileTimezone(),
  )
})

const areNumberArraysEqual = (left: number[], right: number[]) => (
  left.length === right.length && left.every((value, index) => value === right[index])
)

const buildPayload = (): ScheduleDateTimeInputPayload => ({
  startDate: startDate.value,
  endDate: endDate.value,
  selectedDays: [...selectedDays.value],
  selectedWeekdays: [...selectedWeekdays.value],
  selectedTime: {
    timeFrom: timeFrom.value,
    timeTo: timeTo.value,
  },
  repeatOptions: { ...repeatOptions.value },
  summary: summary.value,
})

const isSamePayload = (left: ScheduleDateTimeInputPayload, right: ScheduleDateTimeInputPayload) => (
  left.startDate === right.startDate
  && (left.endDate ?? 0) === (right.endDate ?? 0)
  && left.summary === right.summary
  && left.selectedTime.timeFrom === right.selectedTime.timeFrom
  && left.selectedTime.timeTo === right.selectedTime.timeTo
  && left.repeatOptions.repeat === right.repeatOptions.repeat
  && left.repeatOptions.repeatDays === right.repeatOptions.repeatDays
  && areNumberArraysEqual(left.selectedDays, right.selectedDays)
  && areNumberArraysEqual(left.selectedWeekdays, right.selectedWeekdays)
)

const isSyncingFromProps = ref(false)
const isDateInputFocused = ref(false)
const isEndDateInputFocused = ref(false)

const hasIncompleteDateInput = (): boolean => (
  Boolean(date.value && !isCompleteDateInput(date.value))
  || Boolean(
    endDateMode.value === 'custom'
    && endDateInput.value
    && !isCompleteDateInput(endDateInput.value),
  )
)

const eventInputValue = (event: Event, fallback: string): string => {
  const value = (event.target as { value?: string } | null)?.value
  return typeof value === 'string' ? value : fallback
}

const resolveDateInput = (
  raw: string,
  shouldClamp: boolean,
  minDate: string,
  fallback: string,
): string => {
  const sanitized = sanitizeDateInputYear(raw)

  if (!canSyncDateInputFromModel(sanitized)) {
    return shouldClamp ? fallback : sanitized
  }

  if (shouldClamp && isCompleteDateInput(sanitized)) {
    return clampDateInputToMin(sanitized, minDate)
  }

  return sanitized
}

const dateModel = computed({
  get: () => date.value,
  set: (value: string | number) => {
    if (isDateInputFocused.value) {
      return
    }
    date.value = sanitizeDateInputYear(String(value ?? ''))
  },
})

const endDateModel = computed({
  get: () => endDateInput.value,
  set: (value: string | number) => {
    if (isEndDateInputFocused.value) {
      return
    }
    endDateInput.value = sanitizeDateInputYear(String(value ?? ''))
  },
})

const applySanitizedDateInput = (
  event: Event,
  updateValue: (value: string) => void,
) => {
  const input = event.target as HTMLInputElement
  const sanitized = sanitizeDateInputYear(input.value)
  if (sanitized === input.value) {
    return
  }

  updateValue(sanitized)
  input.value = sanitized
}

const onDateInput = (event: Event) => {
  if (isDateInputFocused.value) {
    return
  }

  applySanitizedDateInput(event, (value) => {
    date.value = value
  })
}

const onEndDateInput = (event: Event) => {
  if (isEndDateInputFocused.value) {
    return
  }

  applySanitizedDateInput(event, (value) => {
    endDateInput.value = value
  })
}

const onDateBlur = (event: FocusEvent) => {
  isDateInputFocused.value = false
  date.value = resolveDateInput(
    eventInputValue(event, date.value),
    true,
    dateMin.value,
    props.modelValue.startDate
      ? formatTimestampToDateInput(props.modelValue.startDate)
      : today.value,
  )
}

const onEndDateBlur = (event: FocusEvent) => {
  isEndDateInputFocused.value = false
  endDateInput.value = resolveDateInput(
    eventInputValue(event, endDateInput.value),
    true,
    endDateMin.value,
    props.modelValue.endDate
      ? formatTimestampToDateInput(props.modelValue.endDate)
      : '',
  )
}

const tryEmitModelValue = () => {
  if (isSyncingFromProps.value || hasIncompleteDateInput()) {
    return
  }

  if (isScheduleTimeUnset(timeFrom.value) || isScheduleTimeUnset(timeTo.value)) {
    return
  }

  const payload = buildPayload()
  if (isSamePayload(payload, props.modelValue)) {
    return
  }

  emit('update:modelValue', payload)
}

watch(isTodaySelected, (valueNew) => {
  if (!valueNew) {
    return
  }

  const nextDefaults = getDefaultScheduleTimeRange(now.value)
  timeFrom.value = nextDefaults.timeFrom
  timeTo.value = nextDefaults.timeTo
})

watch(
  [isTimeFormatInvalid, isPastOnceScheduleStart],
  ([formatInvalid, pastStart]) => {
    emit('update:isTimeInvalid', formatInvalid || pastStart)
  },
  { immediate: true },
)

watch(
  [repeatOptions, startDate, endDate, selectedDays, selectedWeekdays, timeFrom, timeTo, summary],
  tryEmitModelValue,
  { deep: true },
)

watch(() => props.modelValue, (modelValue) => {
  isSyncingFromProps.value = true

  if (
    repeatOptions.value.repeat !== modelValue.repeatOptions.repeat
    || repeatOptions.value.repeatDays !== modelValue.repeatOptions.repeatDays
  ) {
    repeatOptions.value = { ...modelValue.repeatOptions }
  }

  if (!areNumberArraysEqual(selectedWeekdays.value, modelValue.selectedWeekdays)) {
    selectedWeekdays.value = [...modelValue.selectedWeekdays]
  }

  const nextTimeFrom = isScheduleTimeUnset(modelValue.selectedTime.timeFrom)
    ? timeFrom.value
    : modelValue.selectedTime.timeFrom
  const nextTimeTo = isScheduleTimeUnset(modelValue.selectedTime.timeTo)
    ? timeTo.value
    : modelValue.selectedTime.timeTo

  if (timeFrom.value !== nextTimeFrom) {
    timeFrom.value = nextTimeFrom
  }

  if (timeTo.value !== nextTimeTo) {
    timeTo.value = nextTimeTo
  }

  if (!areNumberArraysEqual(selectedDays.value, modelValue.selectedDays)) {
    selectedDays.value = [...modelValue.selectedDays]
  }

  if (modelValue.startDate && !isDateInputFocused.value) {
    const formattedStartDate = formatTimestampToDateInput(modelValue.startDate)
    if (canSyncDateInputFromModel(date.value) && date.value !== formattedStartDate) {
      date.value = formattedStartDate
    }
  }

  if (modelValue.endDate && !isEndDateInputFocused.value) {
    endDateMode.value = 'custom'
    const formattedEndDate = formatTimestampToDateInput(modelValue.endDate)
    if (canSyncDateInputFromModel(endDateInput.value) && endDateInput.value !== formattedEndDate) {
      endDateInput.value = formattedEndDate
    }
  } else if (endDateMode.value !== 'custom' && !isEndDateInputFocused.value) {
    endDateMode.value = 'always'
    endDateInput.value = ''
  }

  void nextTick(() => {
    isSyncingFromProps.value = false
    tryEmitModelValue()
  })
}, { immediate: true, deep: true })

watch(endDateMode, (value) => {
  if (value === 'always') {
    endDateInput.value = ''
  }
})

watch(date, (newDate) => {
  if (!isCompleteDateInput(newDate) || !isCompleteDateInput(endDateInput.value)) {
    return
  }

  const clampedEndDate = clampDateInputToMin(endDateInput.value, newDate)
  if (clampedEndDate !== endDateInput.value) {
    endDateInput.value = clampedEndDate
  }
})

watch(periodMode, (value) => {
  if (value === PeriodMode.EVERY_SPECIAL_MONTH_DAY) {
    selectedDays.value = isCompleteDateInput(date.value)
      ? [dayjs.tz(date.value).date()]
      : []
    endDateMode.value = 'always'
    endDateInput.value = ''
  } else {
    selectedDays.value = []
  }
})

let actualizeNowInterval: NodeJS.Timeout

onBeforeMount(() => {
  actualizeNowInterval = setInterval(() => {
    now.value = dayjs().tz(getProfileTimezone())
    today.value = now.value.format('YYYY-MM-DD')
  }, _15_MINUTES_IN_MS)
})

onMounted(() => {
  nextTick(() => {
    tryEmitModelValue()
  })
})

onBeforeUnmount(() => {
  clearInterval(actualizeNowInterval)
})

</script>
